// "More similar products" button for the new product list grid.
//
// When a collection has custom.collection_parente set (and the section setting is on),
// each product-type row swaps its Load More button for a "See more similar products" one
// once the current collection is exhausted. Clicking it appends products of the same type
// from the parent collection right after the current ones, excluding the ones already listed.
//
// This module owns the eligibility check, the scheduling and the button state. It never
// imports from collections-fetch.ts (an entry) — the entry injects its query helpers.
import { trackEvent } from '../utils/analytics';

export interface IdsPage {
  ids: string[];
  // Edge cursor of each id, same order — lets a listing resume right before any id
  cursors: string[];
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface ParentCollectionDeps {
  // All product ids of the current collection, unfiltered (one page)
  fetchCurrentIds(cursor: string | null): Promise<IdsPage>;
  // Product ids of the parent collection for a section type, with active filters (one page)
  fetchParentIds(sectionType: string, cursor: string | null): Promise<IdsPage>;
}

// Minimal manager surface this module needs — ProductTypeManager satisfies it structurally
export interface ParentAwareManager {
  section: HTMLElement | null;
  sectionType: string;
  parentGeneration: number;
  isParentSource(): boolean;
  activateParentSource(startCursor: string | null): void;
}

// Where the parent listing should resume: cursor right before its next non-excluded product
export interface ParentStart {
  cursor: string | null;
}

interface ParentCollectionConfig {
  handle: string;
  label: string;
}

// Safety caps — a page is 250 ids, so these bound the worst case to a handful of light requests
const MAX_CURRENT_ID_PAGES = 10;
const MAX_PARENT_CHECK_PAGES = 4;

const LOAD_MORE_SELECTOR = '[data-load-more]';
// Set on the section while its button offers the parent products
const OFFER_ATTR = 'parentOffer';
// Where the parent listing starts (empty = from the first product)
const START_CURSOR_ATTR = 'parentStartCursor';
// The button's original label, kept so it can be put back
const DEFAULT_LABEL_ATTR = 'defaultLabel';

function readConfig(root: HTMLElement): ParentCollectionConfig | null {
  const handle = root.dataset.parentHandle;
  const label = root.dataset.parentLabel;
  if (!handle || !label) return null;
  return { handle, label };
}

// requestIdleCallback is missing on Safari — fall back to a short timeout
function whenIdle(): Promise<void> {
  return new Promise((resolve) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => resolve(), { timeout: 2000 });
    } else {
      setTimeout(resolve, 200);
    }
  });
}

export class ParentCollection {
  readonly handle: string;
  private config: ParentCollectionConfig;
  private deps: ParentCollectionDeps;
  private excludedIds: Promise<Set<string>> | null = null;
  // Gate: no eligibility check runs before every row's initial products are rendered
  private gate: Promise<void>;
  private openGate!: () => void;
  // Checks run one at a time so they never compete with each other for the network
  private queue: Promise<void> = Promise.resolve();

  static fromDom(root: HTMLElement | null, deps: ParentCollectionDeps): ParentCollection | null {
    if (!root) return null;
    const config = readConfig(root);
    return config ? new ParentCollection(config, deps) : null;
  }

  private constructor(config: ParentCollectionConfig, deps: ParentCollectionDeps) {
    this.config = config;
    this.handle = config.handle;
    this.deps = deps;
    this.gate = new Promise((resolve) => { this.openGate = resolve; });
  }

  // Called once every manager has kicked off its initial fetch
  start(initialLoads: Promise<unknown>[]): void {
    Promise.allSettled(initialLoads).then(() => this.openGate());
  }

  // Ids of the current collection, fetched once and shared by every row
  getExcludedIds(): Promise<Set<string>> {
    if (!this.excludedIds) {
      this.excludedIds = (async () => {
        const ids = new Set<string>();
        let cursor: string | null = null;
        for (let page = 0; page < MAX_CURRENT_ID_PAGES; page++) {
          const result = await this.deps.fetchCurrentIds(cursor);
          result.ids.forEach((id) => ids.add(id));
          if (!result.hasNextPage) break;
          cursor = result.endCursor;
        }
        return ids;
      })().catch((error) => {
        // Let a later call retry instead of caching the failure
        this.excludedIds = null;
        throw error;
      });
    }
    return this.excludedIds;
  }

  // Capture phase: the Load More button's own listener must not run while it offers the parent products
  bindSection(manager: ParentAwareManager): void {
    manager.section?.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest(LOAD_MORE_SELECTOR);
      if (!button || !manager.section?.dataset[OFFER_ATTR]) return;
      event.preventDefault();
      event.stopPropagation();
      this.activate(manager);
    }, true);
  }

  // The current collection is exhausted for this row: offer the parent products if it has more
  requestOffer(manager: ParentAwareManager): void {
    const generation = manager.parentGeneration;
    const isStale = (): boolean =>
      manager.parentGeneration !== generation || manager.isParentSource() || !manager.section?.isConnected;

    this.queue = this.queue
      .then(() => this.gate)
      .then(() => whenIdle())
      .then(async () => {
        if (isStale() || this.hasOffer(manager)) return;
        const start = await this.findNextStart(manager.sectionType, null);
        if (!start || isStale() || this.hasOffer(manager)) return;
        this.showOffer(manager, start);
      })
      .catch((error) => {
        console.warn('Parent collection check failed', error);
      });
  }

  private hasOffer(manager: ParentAwareManager): boolean {
    return !!manager.section?.dataset[OFFER_ATTR];
  }

  // A reset or an activation puts the Load More button back to its normal label
  clearOffer(manager: ParentAwareManager): void {
    const section = manager.section;
    if (!section) return;
    delete section.dataset[OFFER_ATTR];
    delete section.dataset[START_CURSOR_ATTR];
    const button = section.querySelector<HTMLButtonElement>(LOAD_MORE_SELECTOR);
    const label = button?.dataset[DEFAULT_LABEL_ATTR];
    if (button && label !== undefined) button.textContent = label;
  }

  // Skips the parent's already-listed products with light id-only pages, so the full
  // listing never pages through them. Null when the parent has nothing new after `from`.
  async findNextStart(sectionType: string, from: string | null): Promise<ParentStart | null> {
    const excluded = await this.getExcludedIds();
    let cursor = from;
    for (let page = 0; page < MAX_PARENT_CHECK_PAGES; page++) {
      const result = await this.deps.fetchParentIds(sectionType, cursor);
      const index = result.ids.findIndex((id) => !excluded.has(id));
      if (index === 0) return { cursor };
      if (index > 0) return { cursor: result.cursors[index - 1] };
      if (!result.hasNextPage) return null;
      cursor = result.endCursor;
    }
    return null;
  }

  private showOffer(manager: ParentAwareManager, start: ParentStart): void {
    const section = manager.section;
    const row = section?.querySelector('.load-more-row');
    const button = row?.querySelector<HTMLButtonElement>(LOAD_MORE_SELECTOR);
    if (!section || !row || !button) return;
    if (button.dataset[DEFAULT_LABEL_ATTR] === undefined) button.dataset[DEFAULT_LABEL_ATTR] = button.textContent ?? '';
    button.textContent = this.config.label;
    section.dataset[OFFER_ATTR] = 'true';
    section.dataset[START_CURSOR_ATTR] = start.cursor ?? '';
    row.classList.remove('--hidden');
    row.querySelector('.loader')?.classList.add('--hidden');
    button.classList.remove('--hidden');
  }

  private activate(manager: ParentAwareManager): void {
    if (manager.isParentSource() || !manager.section) return;
    const startCursor = manager.section.dataset[START_CURSOR_ATTR] || null;
    this.clearOffer(manager);

    trackEvent('parent_collection_click', {
      collection: manager.section.closest<HTMLElement>('[data-collection-handle]')?.dataset.collectionHandle ?? '',
      parent_collection: this.handle,
      section_type: manager.sectionType
    });

    manager.activateParentSource(startCursor);
  }
}
